<template>
  <div class="ct-wrap">
    <f7-button v-for="t in tipos" :key="t.key" small :class="['ct-btn', { 'ct-btn-active': activePanel === t.key }]" @click="openPanel(t.key)">
      <span>{{ t.icon }}</span> {{ t.label }}
    </f7-button>
    <f7-button small class="ct-btn ct-btn-ia" @click="$emit('open-tutor')">🤖 Tutor IA</f7-button>

    <!-- La forma de la ventana va en un atributo y no en `:class` (#1027). Al cambiar una clase, Vue
         reescribe el atributo `class` entero y se lleva la `modal-in` que pone Framework7: la ventana
         desaparecía al pasar a pantalla completa, pero para Framework7 ya no estaba abierta, así que
         ninguna salida la cerraba y la capa gris se quedaba sobre la calculadora hasta recargar. -->
    <f7-popup v-for="t in tipos" :key="'pop-'+t.key" ref="popups" :opened="activePanel === t.key" close-on-escape @popup:close="fullscreen = false; activePanel = null; expandedId = null" class="ct-popup" :data-ct-forma="fullscreen ? 'completa' : (lateral ? 'lateral' : 'ventana')">
      <f7-page :page-content="false">
        <f7-navbar :title="t.icon + ' ' + t.label + ' — ' + modulo.toUpperCase()" style="display:none"></f7-navbar>
        <!-- Header con gradiente -->
        <div class="ct-hdr" :style="'background:'+t.gradient">
          <div class="ct-hdr-top">
            <div class="ct-hdr-left">
              <span class="ct-hdr-ico">{{ t.icon }}</span>
              <div>
                <div class="ct-hdr-title">{{ t.label }}</div>
                <div class="ct-hdr-sub">{{ modulo.toUpperCase() }}</div>
              </div>
            </div>
            <div class="ct-hdr-right">
              <span class="ct-hdr-badge">{{ filteredItems(t.key).length }} {{ t.badge }}</span>
              <button v-roles="['ADMIN']" class="ct-hdr-add" @click="startNew(t.key)">+</button>
              <button class="ct-hdr-close" @click="fullscreen = !fullscreen">{{ fullscreen ? '⇖' : '⛶' }}</button>
              <button class="ct-hdr-close" @click="fullscreen = false; activePanel = null">✕</button>
            </div>
          </div>
          <!-- Buscador -->
          <div class="ct-search-wrap">
            <span class="ct-search-ico">🔍</span>
            <input class="ct-search" type="text" :placeholder="'Buscar en ' + t.label + '...'" :value="searchQuery[t.key] || ''" @input="$set(searchQuery, t.key, $event.target.value)" />
            <button v-if="searchQuery[t.key]" class="ct-search-x" @click="$set(searchQuery, t.key, '')">✕</button>
          </div>
        </div>

        <f7-page-content class="ct-body">
          <!-- Loading -->
          <div v-if="loading" class="ct-loading"><f7-preloader></f7-preloader></div>

          <!-- Formulario nuevo (solo admin) -->
          <div v-roles="['ADMIN']" v-if="editingId === '__new__' && editingTipo === t.key" class="ct-new-form">
            <div class="ct-new-title">Nuevo {{ t.label }}</div>
            <div class="ct-form-field">
              <label>Título</label>
              <input type="text" :value="draft.titulo || ''" @input="draft.titulo = $event.target.value" placeholder="Título del contenido..." />
            </div>
            <div class="ct-form-field">
              <label>Contenido (HTML)</label>
              <textarea :value="draft.contenido || ''" @input="draft.contenido = $event.target.value" placeholder="Contenido en HTML..." rows="8"></textarea>
            </div>
            <div class="ct-form-btns">
              <button class="ct-fbtn ct-fbtn-ok" @click="saveNew(t.key)" :disabled="!isDraftValid()">Guardar</button>
              <button class="ct-fbtn ct-fbtn-cancel" @click="cancelEdit">Cancelar</button>
            </div>
          </div>

          <!-- Contador -->
          <div v-if="!loading && filteredItems(t.key).length > 0" class="ct-counter">
            <span v-if="searchQuery[t.key]">{{ filteredItems(t.key).length }} resultados para "{{ searchQuery[t.key] }}"</span>
            <span v-else>{{ filteredItems(t.key).length }} {{ t.badge }}</span>
          </div>

          <!-- Vacio -->
          <div v-if="!loading && filteredItems(t.key).length === 0 && editingId !== '__new__'" class="ct-empty">
            <span style="font-size:32px;opacity:.3">{{ t.icon }}</span>
            <p>Sin contenido disponible para {{ modulo.toUpperCase() }}</p>
          </div>

          <!-- Lista de contenidos -->
          <template v-if="!loading">
            <div v-for="(item, idx) in filteredItems(t.key)" :key="item.id || item._id || idx"
              class="ct-card" :class="{'ct-card-open': expandedId === (item.id || (item._id && item._id.$oid ? item._id.$oid : item._id)), 'ct-card-global': item.modulo === 'global'}"
              :style="'border-left-color:'+t.color">
              <div class="ct-card-hdr">
                <div class="ct-card-hdr-main" @click="toggleExpand(item)">
                  <span class="ct-card-num" :style="'background:'+t.color">{{ idx+1 }}</span>
                  <div class="ct-card-title-area">
                    <span v-if="item.modulo === 'global'" class="ct-card-cat" style="color:#64748b">GLOBAL</span>
                    <div class="ct-card-title">{{ (item.titulo || item.contenido || '') | truncate(120) }}</div>
                  </div>
                  <span class="ct-card-chev">{{ expandedId === (item.id || (item._id && item._id.$oid ? item._id.$oid : item._id)) ? '▲' : '▼' }}</span>
                </div>
                <div style="display:flex;justify-content:center;padding:5px;gap:5px;">
                  <button v-roles="['ADMIN']" @click.stop="startEdit(item, t.key)" style="background:#fff;border:1px solid #ddd;padding:2px 6px;font-size:10px;border-radius:3px;cursor:pointer;">✏️</button>
                  <button v-roles="['ADMIN']" @click.stop="confirmDelete(item)" style="background:#fff;border:1px solid #ddd;padding:2px 6px;font-size:10px;border-radius:3px;cursor:pointer;">🗑️</button>
                  <button @click.stop="copyToClipboard(item)" style="background:#fff;border:1px solid #ddd;padding:2px 6px;font-size:10px;border-radius:3px;cursor:pointer;">📋</button>
                </div>
              </div>
              <div v-if="expandedId === (item.id || (item._id && item._id.$oid ? item._id.$oid : item._id))" class="ct-card-body">
                <!-- Modo edición admin -->
                <template v-if="editingId !== null && editingId === (item.id || (item._id && item._id.$oid ? item._id.$oid : item._id))">
                  <div class="ct-form-field">
                    <label>Título</label>
                    <input type="text" :value="draft.titulo || ''" @input="draft.titulo = $event.target.value" />
                  </div>
                  <div class="ct-form-field">
                    <label>Contenido (HTML)</label>
                    <textarea :value="draft.contenido || ''" @input="draft.contenido = $event.target.value" rows="8"></textarea>
                  </div>
                  <div class="ct-form-btns">
                    <button class="ct-fbtn ct-fbtn-ok" @click="saveEdit(item)">Guardar</button>
                    <button class="ct-fbtn ct-fbtn-cancel" @click="cancelEdit">Cancelar</button>
                  </div>
                </template>
                <!-- Modo lectura -->
                <template v-else>
                  <div class="ct-card-content"><div v-html="purify(item.contenido)"></div></div>
                </template>
              </div>
            </div>
          </template>
        </f7-page-content>
      </f7-page>
    </f7-popup>
  </div>
</template>

<script>
import TarifaContenido from './../../../js/db/TarifaContenido.js';
import cerrojoDeLaSesion from './../../../js/utils/cerrojoDeLaSesion.js';
import { atarALaSesion } from './../../../js/utils/sesionDePeticiones.js';
import DOMPurify from 'dompurify';

export default {
  props: {
    modulo: { type: String, required: true },
    // Abre el contenido en un panel lateral, a la derecha, en vez de en una ventana centrada: es lo
    // que pide el diseño nuevo de la calculadora (#467) para consultar sin perder de vista el
    // cálculo. Sin la prop, la ventana es la de siempre. «Pantalla completa» sigue funcionando y
    // vuelve a la ventana centrada grande.
    lateral: { type: Boolean, default: false }
  },
  filters: {
    truncate: function(val, len) {
      if (!val) return '';
      // Strip HTML tags for truncation display
      var text = val.replace(/<[^>]+>/g, '');
      return text.length > len ? text.substring(0, len) + '...' : text;
    }
  },
  data: function() {
    return {
      activePanel: null,
      searchQuery: {},
      loading: false,
      cache: {}, // { 'modulo|tipo': [items] } — incluye módulo + global concatenados
      editingId: null,
      editingTipo: null,
      draft: {},
      expandedId: null,
      fullscreen: false,
      tipos: [
        { key: 'INFO', icon: '💼', label: 'Info Oferta', color: '#2563eb', gradient: 'linear-gradient(135deg,#1d4ed8,#3b82f6)', badge: 'fichas' },
        { key: 'NORM', icon: '📜', label: 'Normativa', color: '#dc2626', gradient: 'linear-gradient(135deg,#b91c1c,#ef4444)', badge: 'normas' },
        { key: 'DOCM', icon: '📋', label: 'Documentación', color: '#16a34a', gradient: 'linear-gradient(135deg,#15803d,#22c55e)', badge: 'documentos' },
        { key: 'CON', icon: '🎓', label: 'Conocimiento', color: '#7c3aed', gradient: 'linear-gradient(135deg,#6d28d9,#8b5cf6)', badge: 'articulos' }
      ]
    };
  },
  beforeDestroy: function() {
    // Si la calculadora se va con un panel abierto («Atrás», el botón atrás del navegador, cerrar la
    // ventana de la oferta), Vue destruye el panel sin cerrarlo y Framework7 deja puesta la capa gris
    // y el bloqueo del desplazamiento sobre la pantalla a la que se vuelve. Se cierra antes, sin
    // animación, para que Framework7 lo recoja todo (#1027).
    (this.$refs.popups || []).forEach(function(popup) { popup.close(false); });
    this.cache = {};
  },
  methods: {
    cacheKey: function(tipo) { return this.modulo + '|' + tipo; },
    purify: function(html) { return DOMPurify.sanitize(html || ''); },
    invalidateCache: function(tipo) { this.$delete(this.cache, this.cacheKey(tipo)); },
    openPanel: function(key) {
      if (this.activePanel === key) { this.activePanel = null; return; }
      this.activePanel = key;
      this.expandedId = null;
      this.fullscreen = false;
      this.cancelEdit();
      if (!this.cache[this.cacheKey(key)]) {
        this.loadItems(key);
      }
    },
    loadItems: function(tipo) {
      var self = this;
      var ck = self.cacheKey(tipo);
      // #663 · El cerrojo de la sesión: si la sesión cambia en vuelo, las peticiones no acaban y el
      // `finally` del `Promise.all` que lo soltaba no corre.
      cerrojoDeLaSesion(self, 'loading');
      var promises = [
        TarifaContenido.listar({ modulo: self.modulo, tipo: tipo }).catch(function() { return null; })
      ];
      // Si el módulo activo ES 'global', no relanzar la misma query (evita duplicados)
      if (self.modulo !== 'global') {
        promises.push(
          TarifaContenido.listar({ modulo: 'global', tipo: tipo }).catch(function() { return null; })
        );
      }
      // Atado a la sesión: el `catch` de cada consulta no corre si cambia en vuelo, así que el
      // `Promise.all` rechazaba con la cancelación y su `catch` dejaba vacía en la caché de la
      // sesión siguiente la pestaña que estaba cargando.
      atarALaSesion(Promise.all(promises)).then(function(results) {
        var byOrden = function(a, b) { return (a.orden || 0) - (b.orden || 0); };
        var moduloItems = (results[0] && (results[0].list || results[0])) || [];
        var globalItems = (results[1] && (results[1].list || results[1])) || [];
        if (!Array.isArray(moduloItems)) moduloItems = [];
        if (!Array.isArray(globalItems)) globalItems = [];
        moduloItems.sort(byOrden);
        globalItems.sort(byOrden);
        self.$set(self.cache, ck, moduloItems.concat(globalItems));
      }).catch(function() {
        self.$set(self.cache, ck, []);
      }).finally(function() {
        self.loading = false;
      });
    },
    filteredItems: function(key) {
      var list = this.cache[this.cacheKey(key)] || [];
      var q = (this.searchQuery[key] || '').toLowerCase();
      if (!q) return list;
      return list.filter(function(item) {
        return [item.titulo, item.contenido].filter(Boolean).join(' ').toLowerCase().indexOf(q) >= 0;
      });
    },
    isDraftValid: function() {
      return (this.draft.titulo || '').trim().length > 2 && (this.draft.contenido || '').trim().length > 5;
    },
    startNew: function(tipo) {
      this.editingId = '__new__';
      this.editingTipo = tipo;
      this.draft = { titulo: '', contenido: '' };
    },
    startEdit: function(item, tipo) {
      this.editingId = item.id || (item._id && item._id.$oid ? item._id.$oid : item._id);
      this.editingTipo = tipo;
      this.draft = JSON.parse(JSON.stringify(item));
    },
    cancelEdit: function() { this.editingId = null; this.editingTipo = null; this.draft = {}; },
    saveNew: function(tipo) {
      var self = this;
      var data = {
        titulo: this.draft.titulo,
        contenido: this.draft.contenido,
        tipo: tipo,
        modulo: this.modulo,
        orden: 99,
        activo: true,
        fechaCreacion: new Date().toISOString()
      };
      TarifaContenido.crear(data).then(function() {
        self.cancelEdit();
        self.invalidateCache(tipo);
        self.loadItems(tipo);
        self.$f7.toast.create({ text: 'Guardado', position: 'center', closeTimeout: 1500 }).open();
      }).catch(function(e) { self.$f7.dialog.alert(e.message || 'Error al guardar'); });
    },
    saveEdit: function(item) {
      var self = this;
      var id = item.id || (item._id && item._id.$oid ? item._id.$oid : item._id);
      var tipoAfectado = item.tipo;
      TarifaContenido.actualizar(id, this.draft).then(function() {
        self.cancelEdit();
        self.invalidateCache(tipoAfectado);
        self.loadItems(tipoAfectado);
        self.$f7.toast.create({ text: 'Actualizado', position: 'center', closeTimeout: 1500 }).open();
      }).catch(function(e) { self.$f7.dialog.alert(e.message || 'Error al actualizar'); });
    },
    confirmDelete: function(item) {
      var self = this;
      var id = item.id || (item._id && item._id.$oid ? item._id.$oid : item._id);
      var tipoAfectado = item.tipo;
      self.$f7.dialog.confirm('¿Eliminar este contenido?', function() {
        TarifaContenido.eliminar(id).then(function() {
          self.invalidateCache(tipoAfectado);
          self.loadItems(tipoAfectado);
          self.$f7.toast.create({ text: 'Eliminado', position: 'center', closeTimeout: 1500 }).open();
        }).catch(function(e) { self.$f7.dialog.alert(e.message || 'Error al eliminar'); });
      });
    },
    toggleExpand: function(item) {
      var id = item.id || (item._id && item._id.$oid ? item._id.$oid : item._id);
      this.expandedId = this.expandedId === id ? null : id;
    },
    copyToClipboard: function(item) {
      // Copiar texto plano (sin HTML)
      var text = (item.titulo || '') + '\n\n' + (item.contenido || '').replace(/<[^>]+>/g, '');
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
        this.$f7.toast.create({ text: 'Copiado al portapapeles', position: 'center', closeTimeout: 1500 }).open();
      }
    }
  }
};
</script>

<style scoped>
.ct-wrap{display:flex;align-items:center;gap:5px;flex-wrap:wrap}
.ct-btn{--f7-button-font-size:11px;--f7-button-font-weight:600;--f7-button-padding-horizontal:10px;--f7-button-height:28px;--f7-button-bg-color:rgba(255,255,255,.07);--f7-button-text-color:rgba(255,255,255,.75);border:1px solid rgba(255,255,255,.13);border-radius:5px}
.ct-btn:hover,.ct-btn-active{--f7-button-bg-color:rgba(232,144,26,.3);--f7-button-text-color:#fff;border-color:rgba(232,144,26,.7)}
.ct-btn-ia{--f7-button-bg-color:rgba(109,40,217,.22);--f7-button-text-color:#c4b5fd;border-color:rgba(109,40,217,.4)}
.ct-btn-ia:hover{--f7-button-bg-color:rgba(109,40,217,.45);--f7-button-text-color:#fff}
.ct-popup{--f7-popup-tablet-width:800px;--f7-popup-tablet-height:90vh}
.ct-popup[data-ct-forma="completa"]{--f7-popup-tablet-width:95vw;--f7-popup-tablet-height:95vh}
/* Panel lateral (#467). La especificidad (`.popup.ct-popup` y la forma, más el atributo del scoped)
   es la que gana a la regla de tableta de Framework7 (`.popup:not(.popup-tablet-fullscreen)`), que
   lo centraría; y la entrada es desde la derecha en vez de desde abajo. */
.popup.ct-popup[data-ct-forma="lateral"]{left:auto!important;right:0;top:0!important;width:460px!important;max-width:100vw!important;height:100%!important;margin:0!important;border-radius:0!important;transform:translate3d(100%,0,0)}
.popup.ct-popup[data-ct-forma="lateral"].modal-in{transform:translate3d(0,0,0)}
.popup.ct-popup[data-ct-forma="lateral"].modal-out{transform:translate3d(100%,0,0)}

/* Header */
.ct-hdr{padding:14px 16px 12px;flex-shrink:0}
.ct-hdr-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px}
.ct-hdr-left{display:flex;align-items:center;gap:10px}
.ct-hdr-ico{font-size:24px}
.ct-hdr-title{font-size:16px;font-weight:800;color:#fff;letter-spacing:.02em}
.ct-hdr-sub{font-size:10px;color:rgba(255,255,255,.6);margin-top:1px}
.ct-hdr-right{display:flex;align-items:center;gap:6px}
.ct-hdr-badge{background:rgba(255,255,255,.2);border-radius:12px;padding:3px 10px;font-size:10px;font-weight:700;color:#fff}
.ct-hdr-add,.ct-hdr-close{width:28px;height:28px;border-radius:6px;border:1px solid rgba(255,255,255,.25);background:rgba(255,255,255,.1);color:#fff;font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s}
.ct-hdr-add:hover,.ct-hdr-close:hover{background:rgba(255,255,255,.25)}
.ct-search-wrap{position:relative;display:flex;align-items:center}
.ct-search-ico{position:absolute;left:10px;font-size:13px}
.ct-search{width:100%;padding:9px 32px 9px 32px;border:none;border-radius:8px;font-size:13px;background:rgba(255,255,255,.9);color:#1e293b;outline:none;box-shadow:0 2px 8px rgba(0,0,0,.1)}
.ct-search:focus{background:#fff;box-shadow:0 2px 12px rgba(0,0,0,.15)}
.ct-search-x{position:absolute;right:8px;background:none;border:none;color:#94a3b8;font-size:12px;cursor:pointer;padding:4px}

/* Body */
.ct-body{background:linear-gradient(180deg,#f8fafc,#f0f2f5);padding:10px 14px 60px !important;flex:1;min-height:0}
.ct-popup >>> .page-content{overflow-y:auto!important;-webkit-overflow-scrolling:touch;flex:1;min-height:0}
.ct-card-body{max-height:none;overflow-y:visible}
.ct-loading{text-align:center;padding:40px}
.ct-counter{font-size:11px;color:#64748b;font-weight:600;padding:4px 2px 8px;letter-spacing:.02em}
.ct-empty{text-align:center;padding:40px 20px;display:flex;flex-direction:column;align-items:center;gap:8px;color:#94a3b8;font-size:12px}

/* Card */
.ct-card{background:#fff;border-radius:10px;border:1px solid #e2e8f0;border-left:3px solid;margin-bottom:8px;box-shadow:0 1px 3px rgba(0,0,0,.05);transition:all .2s;overflow:visible}
.ct-card:hover{box-shadow:0 3px 10px rgba(0,0,0,.08);transform:translateY(-1px)}
.ct-card-open{box-shadow:0 4px 16px rgba(0,0,0,.1)}
.ct-card-global{opacity:.85;border-style:dashed;border-left-style:solid}
.ct-card-hdr{display:flex;flex-direction:column;padding:0}
.ct-card-hdr-main{display:flex;align-items:center;gap:12px;padding:12px 16px;cursor:pointer}
.ct-card-num{width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;color:#fff;flex-shrink:0}
.ct-card-title-area{flex:1;min-width:0}
.ct-card-cat{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;display:block;margin-bottom:2px}
.ct-card-title{font-size:13px;font-weight:600;color:#1e293b;line-height:1.4}
.ct-card-chev{font-size:10px;color:#94a3b8;flex-shrink:0;transition:transform .2s}
.ct-card-body{padding:0 12px 12px;border-top:1px solid #f1f5f9}

/* Contenido HTML renderizado */
.ct-card-content{font-size:12.5px;line-height:1.6;color:#334155;padding:10px 4px 40px 4px}
.ct-card-content >>> h3{font-size:14px;font-weight:800;color:#1a2744;margin:12px 0 8px;padding-bottom:4px;border-bottom:1px solid #e2e8f0}
.ct-card-content >>> h4{font-size:12.5px;font-weight:700;color:#374151;margin:10px 0 6px}
.ct-card-content >>> p{margin:4px 0 8px}
.ct-card-content >>> ol{margin:4px 0 8px;padding-left:20px}
.ct-card-content >>> ul{margin:4px 0 8px;padding-left:16px}
.ct-card-content >>> li{margin:3px 0;line-height:1.5}
.ct-card-content >>> b,.ct-card-content >>> strong{font-weight:700;color:#1e293b}
.ct-card-content >>> a{color:#2563eb;text-decoration:underline}

/* Form */
.ct-new-form{background:#fff;border-radius:10px;border:2px dashed #d1d9e0;padding:14px;margin-bottom:12px}
.ct-new-title{font-size:12px;font-weight:700;color:#1a2744;text-transform:uppercase;letter-spacing:.06em;margin-bottom:10px}
.ct-form-field{display:flex;flex-direction:column;gap:3px;margin-bottom:8px}
.ct-form-field label{font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.04em}
.ct-form-field input,.ct-form-field textarea{padding:8px 10px;border:1.5px solid #d1d9e0;border-radius:6px;font-size:12px;font-family:inherit;color:#1e293b;outline:none;resize:vertical;transition:border .15s}
.ct-form-field input:focus,.ct-form-field textarea:focus{border-color:#2563eb}
.ct-form-btns{display:flex;gap:6px}
.ct-fbtn{padding:7px 16px;border:none;border-radius:6px;font-size:11px;font-weight:700;cursor:pointer;font-family:inherit;transition:all .15s}
.ct-fbtn-ok{background:#16a34a;color:#fff}
.ct-fbtn-ok:hover{background:#15803d}
.ct-fbtn-ok:disabled{opacity:.5;cursor:not-allowed}
.ct-fbtn-cancel{background:#f1f5f9;color:#475569;border:1px solid #e2e8f0}
.ct-fbtn-cancel:hover{background:#e2e8f0}
</style>
